/**
 * Update Actions - Snippet editing with UI integration
 *
 * This action handles snippet updates with full UI integration:
 * - Updates the snippet via the API
 * - Shows success/error flags
 * - Updates the snippet in the editor's quick insert menu
 * - Updates the snippet in the store in place (preserves list order)
 *
 * Separated from fetchActions because it has UI dependencies (flags, intl, editor API)
 * that the pure data operations don't need.
 */
import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape } from 'react-intl-next';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import { messages } from '../../modals/SnippetEditorModal.i18n.ts';
import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import type { UpdateSnippetPayload } from '../../services/blockServiceTypes.ts';
import type { Snippet } from '../../snippetTypes.ts';
import { reportError } from '../../reportError.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

import { showSnippetErrorFlag, showSnippetSuccessFlag } from './showSnippetFlags.ts';

/**
 * Options for updateSnippet that enable UI feedback and editor integration
 */
export type UpdateSnippetOptions = {
	/** Flags API for showing success/error notifications */
	flags: WithFlagsProps['flags'];
	/** Intl for message formatting */
	intl: IntlShape;
	/** Editor API (e.g. from EditorContextPanelContext or editorAPIRef.current) for updating snippet in quick insert */
	editorAPI?: PublicPluginAPI<[QuickInsertPlugin]> | null;
};

/**
 * Actions for updating snippets with UI integration.
 */
export const updateActions = {
	/**
	 * Updates an existing snippet and updates the store in place (preserves list order).
	 *
	 * On success:
	 * - Replaces the snippet in the store with the API response
	 * - Shows a success flag (if options provided)
	 * - Adds/updates the snippet in the quick insert menu
	 *
	 * On error:
	 * - Reports to monitoring
	 * - Shows an error flag (if options provided)
	 * - Keeps existing snippets visible (doesn't reset isLoaded)
	 *
	 * @param snippetId - The ID of the snippet to update
	 * @param name - The name of the snippet
	 * @param body - The body of the snippet
	 * @param description - Optional description text
	 * @param options - UI options including flags, intl, and editor API
	 * @returns Promise resolving to the updated snippet, or null on failure
	 */
	updateSnippet:
		(
			snippetId: string,
			name: string,
			body: ADFEntity,
			description?: string,
			options?: UpdateSnippetOptions,
		) =>
		async ({ getState, setState }: StoreActionApi<SnippetsState>): Promise<Snippet | null> => {
			const descriptionValue = description?.trim();
			const params: UpdateSnippetPayload = {
				name,
				...(descriptionValue ? { description: descriptionValue } : {}),
				body: JSON.stringify(body),
			};

			try {
				const snippet = await BlockServiceClient.updateSnippet(snippetId, params);

				const currentSnippets = getState().snippets;
				const updatedSnippets = currentSnippets.map((s) =>
					s.snippetId === snippet.snippetId ? snippet : s,
				);

				setState({ snippets: updatedSnippets });

				if (options) {
					const { flags, intl, editorAPI } = options;
					showSnippetSuccessFlag(
						flags,
						intl.formatMessage(messages.snippetEditorModalEditSuccessFlagTitle, {
							snippetName: snippet.name,
						}),
						'',
					);
					if (editorAPI) {
						// TODO: DELETE old snippet and add new one to quick insert menu
					}
				}
				return snippet;
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));
				reportError(err);

				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(messages.snippetEditorModalEditErrorFlagTitle),
						options.intl.formatMessage(messages.snippetEditorModalEditErrorFlagDescription),
					);
					return null;
				}
				throw err;
			}
		},
};
